/**
 * @class AcademyUspList
 * Sticky accordion list (à la Blueprint Library's "list-02"). The whole
 * block (heading + list + stage) pins at the top; shortly after the pin
 * engages the heading fades out and collapses (0 height), the list rows
 * "nachrücken" (slide up to fill the gap) and end up pinned at the very
 * top on their own — only THEN does the accordion start stepping through
 * rows as the user keeps scrolling, swapping the stage image each time.
 * Each opened row's body text reveals with the same block-sweep effect as
 * the heading (see academy-text-reveal.js). The arrow follows the mouse
 * while the heading is still visible, rotates to "aim" at a hovered row,
 * and latches onto the active row (fast) once the list locks to the top.
 */
if (!customElements.get('academy-usp-list')) {
  class AcademyUspList extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-usp-list]');
      this.items = Array.from(this.querySelectorAll('[data-usp-item]'));
      this.images = Array.from(this.querySelectorAll('[data-usp-image]'));
      this.arrow = this.querySelector('[data-usp-arrow]');
      this.arrowIcon = this.querySelector('[data-usp-arrow-icon]');
      this.heading = this.querySelector('[data-usp-heading]');
      this.headingReveal = this.querySelector('[data-usp-heading-reveal]');
      this.content = this.querySelector('.academy-usp-list__content');
      this.stage = this.querySelector('[data-usp-stage]');
      this.stageInner = this.querySelector('[data-usp-stage-inner]');
      this.pill = this.querySelector('[data-usp-pill]');
      this.pillClip = this.querySelector('[data-usp-pill-clip]');
      this.pillStack = this.querySelector('[data-usp-pill-stack]');
      this.pillRows = Array.from(this.querySelectorAll('.academy-usp-list__pill-row'));
      this.detailsEls = Array.from(this.querySelectorAll('details'));
      this.activeIndex = 0;
      this._scrollIndex = 0;
      this._isListPinnedAtTop = false;

      // Only desktop pointers get the click-to-scroll assist; on touch it
      // fought the user's own scroll (window.scrollTo mid-swipe).
      this._canPointerFine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      // Timestamp + index of the last real tap/click on a row trigger.
      // A scroll-driven open leaves these untouched, so it can be told
      // apart from a user-initiated open without the old async-`toggle`
      // vs. setTimeout race.
      this._userTapAt = -Infinity;
      this._userTapIndex = -1;

      this.setupReveal();
      this.setupUserGestureTracking();
      this.setupStaticTriggers();
      this.setupAccordionSync();
      this.setupToggleInteractions();
      this.setupScrollProgress();
      this.setupStageReveal();
      this.setupArrowEntrance();
      this.setupArrowMouseFollow();
      this.setupStagePointer();
      this.setupPillStack();
      this.updateArrowPosition();
      this.startArrowIdle();

      this._onResize = () => {
        this.updateArrowPosition();
        this.updatePillStack(this.activeIndex, true);
      };
      window.addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      if (this._onScrollEnd) window.removeEventListener('scrollend', this._onScrollEnd);
      clearTimeout(this._commitTimer);
      clearTimeout(this._autoScrollTimeout);
      if (this._stGate) this._stGate.disconnect();
      if (this._scrollTrigger) this._scrollTrigger.kill();
      this.stopArrowIdle();
    }

    // Own, independent scroll-into-view trigger — NOT gated behind the
    // heading's `academytextreveal:play` event. That event-dependency was
    // fragile: if the heading reveal ever failed to fire for any reason,
    // the rows/dividers silently never revealed either. A fixed delay
    // (see playItemsReveal's baseDelay) gets the "follows the text" feel
    // without that hard coupling.
    setupReveal() {
      if (!this.list) return;

      this._hasPlayedInitialReveal = false;
      const play = () => {
        if (this._hasPlayedInitialReveal) return;
        this._hasPlayedInitialReveal = true;
        this.list.classList.add('is-revealed'); // CSS-driven divider wipe
        this.playItemsReveal();
        this.playStageReveal();
      };
      // A hash/history jump can land directly in the pinned part of this
      // section, before IntersectionObserver has reported an intersection.
      // Keep the entrance idempotent and callable from that scroll state.
      this.playInitialReveal = play;

      if (!('IntersectionObserver' in window)) {
        play();
        return;
      }

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            play();
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.4 });

      io.observe(this.list);
    }

    // Rows pop in (scale + fade, expo.out) staggered — delayed behind the
    // heading's own reveal — in sync with the CSS divider wipe (same base
    // delay + stagger step), and each row's "+" toggle pops in with a
    // slight elastic rotation shortly after its row. clearProps on
    // completion is essential: GSAP leaves its own inline transform/
    // opacity on each element, and an inline style always beats a
    // stylesheet rule (any specificity) — without clearing it, the CSS
    // `details[open] .academy-usp-list__toggle { transform: rotate(45deg) }`
    // click-to-open rule (and the resting/hover opacity) can never take
    // effect again once this reveal has run.
    playItemsReveal() {
      if (typeof gsap === 'undefined') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!this.items.length) return;

      const baseDelay = 0.85; // starts well after the heading's own reveal
      const itemStagger = 0.32;
      const toggles = this.items
        .map((item) => item.querySelector('.academy-usp-list__toggle'))
        .filter(Boolean);

      gsap.set(this.items, { opacity: 0, scale: 0.85, transformOrigin: 'left center' });
      gsap.to(this.items, {
        opacity: 1,
        scale: 1,
        duration: 0.6,
        ease: 'expo.out',
        stagger: itemStagger,
        delay: baseDelay,
        clearProps: 'opacity,scale,transform',
      });

      if (toggles.length) {
        gsap.set(toggles, { opacity: 0, scale: 0, rotate: -25 });
        gsap.to(toggles, {
          opacity: 1,
          scale: 1,
          rotate: 0,
          duration: 0.6,
          ease: 'elastic.out(1, 0.5)',
          stagger: itemStagger,
          delay: baseDelay + 0.2,
          clearProps: 'opacity,scale,rotate,transform',
        });
      }
    }

    // Records the last genuine tap/click on a row trigger (pointerdown so
    // it lands before the async `toggle`). setupAccordionSync reads this to
    // decide whether an open was user-initiated (→ scroll the page to that
    // row) or scroll-driven (→ do nothing). Replaces the old
    // `_isProgrammaticToggle` flag + 200ms fallback, which lost its race on
    // a janky mobile main thread and let scroll-driven opens call
    // window.scrollTo() straight into the user's swipe.
    setupUserGestureTracking() {
      const mark = (event) => {
        const trigger = event.target.closest('[data-usp-trigger]');
        if (!trigger || !this.contains(trigger)) return;
        this._userTapAt = performance.now();
        this._userTapIndex = parseInt(trigger.dataset.index, 10);
      };
      this.addEventListener('pointerdown', mark, true);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') mark(event);
      }, true);
    }

    // Was this open the direct result of a real tap/click on THIS row
    // (within the last beat), rather than the scroll position stepping
    // into its band? Touch pointers never qualify — the page-scroll assist
    // is desktop-only.
    wasUserInitiated(index) {
      return (
        this._canPointerFine &&
        this._userTapIndex === index &&
        performance.now() - this._userTapAt < 600
      );
    }

    // Items without accordion content render as a plain button (no native
    // `toggle` event to hook into), so they need a direct click handler.
    setupStaticTriggers() {
      this.items.forEach((item) => {
        if (item.querySelector('details')) return;

        const index = parseInt(item.dataset.index, 10);
        const trigger = item.querySelector('[data-usp-trigger]');
        if (!trigger) return;

        trigger.addEventListener('click', () => {
          this.setActive(index);
          if (this._canPointerFine) this.scrollToIndex(index);
        });
      });
    }

    // The toggle's scale/rotate/background is driven entirely from here —
    // NOT from CSS :hover/[open] rules. Those competed on the same
    // `transform` property at equal specificity (hover's scale vs. open's
    // rotate), so whichever was open when you hovered silently won and
    // the other visibly "did nothing". One JS-driven state machine avoids
    // that entirely and composes both at once (e.g. hovering an already-
    // open row correctly shows scaled-up AND rotated).
    setupToggleInteractions() {
      if (typeof gsap === 'undefined') return;

      this.items.forEach((item) => {
        const toggle = item.querySelector('.academy-usp-list__toggle');
        const trigger = item.querySelector('[data-usp-trigger]');
        const details = item.querySelector('details');
        if (!toggle || !trigger) return;

        let hovering = false;
        let pressed = false;

        const apply = () => {
          gsap.to(toggle, {
            scale: pressed ? 0.88 : hovering ? 1.12 : 1,
            y: pressed ? 0 : hovering ? -4 : 0,
            rotate: details && details.open ? 45 : 0,
            backgroundColor: hovering ? '#B5E3EA' : 'rgba(255,255,255,0)',
            opacity: hovering || (details && details.open) ? 1 : 0.5,
            duration: 0.25,
            ease: 'power2.out',
            overwrite: 'auto',
          });
        };

        trigger.addEventListener('mouseenter', () => { hovering = true; apply(); });
        trigger.addEventListener('mouseleave', () => { hovering = false; pressed = false; apply(); });
        trigger.addEventListener('mousedown', () => { pressed = true; apply(); });
        trigger.addEventListener('mouseup', () => { pressed = false; apply(); });
        if (details) details.addEventListener('toggle', apply);
      });
    }

    // Single source of truth for accordion state: whatever opens a
    // <details> — native click, keyboard, or a scroll-driven `.open = true`
    // — ends up here, keeping only one row open and syncing the image/arrow.
    // A user-initiated open (click/keyboard, not our own scroll-driven one)
    // also scrolls the page to that row's matching position, so the two
    // stay in sync regardless of where the user clicked from.
    setupAccordionSync() {
      this.detailsEls.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (details.open) {
            const index = parseInt(details.closest('[data-usp-item]').dataset.index, 10);
            this.detailsEls.forEach((other) => {
              if (other !== details) this.animateRow(other, false);
            });
            this.setActive(index);
            this.bounceStage(1);
            this.playContentReveal(details);
            // Only a real tap/click on this row scrolls the page to it
            // (desktop only — see wasUserInitiated). A scroll-driven open,
            // or any open on touch, never calls window.scrollTo(), so it
            // can't fight the user's own scroll or trap an anchor jump
            // passing through the section.
            if (this.wasUserInitiated(index)) {
              this.scrollToIndex(index);
            }
          } else {
            this.bounceStage(-1);
          }
        });
      });
    }

    // The accordion body's own text reveal (see academy-usp-list.liquid,
    // data-usp-content-reveal) — plays once, the first time that row opens.
    playContentReveal(details) {
      const reveal = details.querySelector('[data-usp-content-reveal]');
      if (reveal && typeof reveal.playReveal === 'function') {
        reveal.playReveal();
      }
    }

    // Heading + list + stage pin together (CSS `position: sticky` on
    // `.sticky`) for the whole scroll track. Shortly after the pin
    // engages, the heading fades AND collapses (height + the flex gap it
    // was taking up) so the rows genuinely slide up to sit at the very
    // top — not just a fade with a lingering gap. Only once that's done
    // does the accordion start stepping through rows for the rest of the
    // scroll range.
    setupScrollProgress() {
      if (typeof ScrollTrigger === 'undefined') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (this.items.length < 2 && !this.heading) return;

      // Mobile: compress the heading fade/collapse into the very first bit
      // of the track so one swipe takes it up and out of the way, instead
      // of ~40svh of scrolling before the list even starts stepping.
      // Desktop keeps the longer, more gradual run.
      if (window.matchMedia('(max-width: 767px)').matches) {
        this._headingFadeStart = 0.015;
        this._headingFadeEnd = 0.09;
      } else {
        this._headingFadeStart = 0.06;
        this._headingFadeEnd = 0.22;
      }
      // Separate enter/exit thresholds for the "list is pinned at top"
      // boolean. A single hard threshold at _headingFadeEnd meant sub-pixel
      // scroll jitter right at that point flipped the state every frame —
      // thrashing onListPinnedAtTop()/closeAllAccordionItems() (arrow snap +
      // stage bounce + row 0 open/close). The dead-band between the two
      // makes the toggle stable without changing where it engages.
      this._pinEnter = this._headingFadeEnd;
      // Wide dead-band: once pinned, the state only flips back to "not
      // pinned" well below the enter point, so easing off the scroll near
      // the section start doesn't flip it every few pixels.
      this._pinExit = Math.max(0.04, this._headingFadeEnd - 0.12);
      // Only a genuine scroll back above the section start (progress ≈ 0)
      // fully resets the accordion. Doing it at _pinExit used to slam every
      // row shut and then re-open (+ scroll) on the way back down, trapping
      // the user in a bounce right at the section's start.
      this._resetBelow = 0.02;
      // Hysteresis for the scroll-driven accordion index (in row-band
      // units): stay on the current row until the scroll position has moved
      // this far PAST the band boundary. Large on purpose — a small value
      // made every few pixels of scroll flip to the next row ("feels like a
      // hurdle, not natural"); this makes each row hold while you read it.
      // Mobile gets a wider dead-band still: a fast/long swipe there was
      // stepping through rows too eagerly and flickering at the boundary.
      this._isMobile = window.matchMedia('(max-width: 767px)').matches;
      // Mobile: moderate dead-band. Stepping is deferred to a settle point
      // and clamped to ±1 per settle (see _commitPendingIndex), so it can't
      // skip rows or race — this just controls how far into a swipe the
      // next row engages.
      this._indexMargin = this._isMobile ? 0.18 : 0.14;

      if (this.heading && typeof gsap !== 'undefined') {
        gsap.set(this.heading, { transformOrigin: 'top left' });
        this._headingHeight = this.heading.offsetHeight;
        this._contentGap = this.content
          ? parseFloat(getComputedStyle(this.content).rowGap) || parseFloat(getComputedStyle(this.content).gap) || 0
          : 0;
      }

      this._scrollTrigger = ScrollTrigger.create({
        trigger: this,
        start: 'top top',
        end: 'bottom bottom',
        onUpdate: (self) => {
          if (this._stIdle) return;
          this.syncScrollState(self, false);

          if (!this._isMobile && this.heading && typeof gsap !== 'undefined') {
            const p = self.progress;
            const fadeP = Math.max(0, Math.min(1, (p - this._headingFadeStart) / (this._headingFadeEnd - this._headingFadeStart)));
            // Only touch the DOM when fadeP actually changed. While the list
            // is pinned (fadeP stays clamped at 1) or fully at rest (0) this
            // now writes nothing per frame — previously it re-set an
            // animated `height`/`marginBottom` on every scroll frame of the
            // whole accordion phase, forcing a layout of the list + stage
            // subtree each time (the scroll "ruckeln").
            if (fadeP !== this._lastHeadingFadeP) {
              this._lastHeadingFadeP = fadeP;
              if (fadeP <= 0) {
                // At rest, never force a height/margin from a cached
                // measurement — let the heading keep its natural CSS layout.
                // A stale/wrong cached height here is what caused the
                // heading to overlap the list below it.
                gsap.set(this.heading, { opacity: 1, scale: 1, y: 0, clearProps: 'height,marginBottom' });
              } else {
                const headingHeight = this._headingHeight || this.heading.scrollHeight;
                gsap.set(this.heading, {
                  opacity: 1 - fadeP,
                  scale: 1 - fadeP * 0.25,
                  y: fadeP * -40, // slides up as it shrinks/fades, not just in place
                  height: Math.round(headingHeight * (1 - fadeP)), // int px → no sub-pixel reflows
                  marginBottom: this._contentGap ? -this._contentGap * fadeP : 0,
                });
              }
            }
          }
        },
        // Refresh also runs after another pinned/scrubbed Upgrade section
        // changed document measurements. Resolve the visual state directly
        // from the current scroll position; never move the page itself.
        onRefresh: (self) => this.syncScrollState(self, true),
      });

      // Mobile: commit the pending row the instant momentum scrolling ends,
      // rather than waiting out the debounce.
      if (this._isMobile && 'onscrollend' in window) {
        this._onScrollEnd = () => {
          clearTimeout(this._commitTimer);
          this._commitPendingIndex();
        };
        window.addEventListener('scrollend', this._onScrollEnd);
      }

      // onUpdate cost gate: while the section sits well outside the viewport
      // just SKIP the heavy per-frame onUpdate body (this._stIdle is checked
      // at the top of onUpdate). The ScrollTrigger itself stays enabled and
      // measured the whole time — previously we disable(false)'d it and
      // enable()+refresh()'d on approach, but on a fast mobile scroll the
      // IntersectionObserver fires late, so the section could be reached
      // while the trigger was still disabled → scrolled in un-pinned, then
      // snapped back when it re-enabled ("zurückgeleitet"), and the
      // mid-scroll refresh() was its own hitch.
      if ('IntersectionObserver' in window) {
        this._stGate = new IntersectionObserver((entries) => {
          this._stIdle = !entries.some((e) => e.isIntersecting);
        }, { rootMargin: '100% 0px 100% 0px' });
        this._stGate.observe(this);
      }
    }

    // Convert the authoritative ScrollTrigger progress into one accordion
    // band. This is deliberately independent of element geometry: sticky
    // descendants move/reparent while other Upgrade sections refresh, but
    // the trigger's own progress always describes the user's real location.
    scrollIndexForProgress(progress) {
      if (!this.items.length) return 0;
      const accordionP = Math.max(0, Math.min(
        1,
        (progress - this._headingFadeEnd) / (1 - this._headingFadeEnd)
      ));
      return Math.min(this.items.length - 1, Math.floor(accordionP * this.items.length));
    }

    // Single state machine for forward scroll, backward scroll, browser
    // history restoration and anchor jumps. It never calls scrollTo(); it
    // only makes the section match the position the browser already chose.
    syncScrollState(self, immediate) {
      const p = self.progress;
      const wasPinned = this._isListPinnedAtTop;
      if (!wasPinned && p >= this._pinEnter) {
        this._isListPinnedAtTop = true;
      } else if (wasPinned && p < this._pinExit) {
        this._isListPinnedAtTop = false;
      }

      if (this._isListPinnedAtTop !== wasPinned) {
        if (this.arrow) this.arrow.classList.toggle('is-pinned', this._isListPinnedAtTop);
        if (this._isMobile) {
          this._settleStage(() => this.collapseHeading(this._isListPinnedAtTop));
        }
        if (this._isListPinnedAtTop) {
          this.playInitialReveal();
          this.onListPinnedAtTop();
        }
      }

      // Only reset above the actual section start. Reversing through the
      // heading transition must preserve the current row, otherwise the
      // accordion visibly closes and reopens at the pin boundary.
      if (!this._isListPinnedAtTop && p <= this._resetBelow) {
        const anyOpen = this.detailsEls.some((d) => d.open);
        if (anyOpen || this.activeIndex !== 0) this.closeAllAccordionItems();
        return;
      }

      if (!this._isListPinnedAtTop || !this.items.length || this._isAutoScrolling) return;

      const target = this.scrollIndexForProgress(p);
      const previous = this._scrollIndex;
      const delta = target - previous;
      let next = previous;

      // A refresh, history restore or anchor can jump over more than one
      // band in a single browser update. Set its final state immediately;
      // replaying intermediate rows here would make the page appear to pull
      // the visitor back through the section. Ordinary neighbouring bands
      // retain a small directional hysteresis against wheel jitter.
      if (immediate || Math.abs(delta) > 1) {
        next = target;
      } else if (delta > 0) {
        const raw = Math.max(0, Math.min(1, (p - this._headingFadeEnd) / (1 - this._headingFadeEnd))) * this.items.length;
        if (raw >= previous + 1 + this._indexMargin) next = target;
      } else if (delta < 0) {
        const raw = Math.max(0, Math.min(1, (p - this._headingFadeEnd) / (1 - this._headingFadeEnd))) * this.items.length;
        if (raw <= previous - this._indexMargin) next = target;
      }

      // Entering the pinned range starts with USP 1 even on desktop. The
      // original implementation did this only on mobile, so desktop could
      // jump straight from a closed list to the second row.
      const activeDetails = this.items[next] && this.items[next].querySelector('details');
      const mustOpen = !!(activeDetails && !activeDetails.open);
      if (next === previous && !mustOpen) return;

      this._scrollIndex = next;
      if (immediate || !this._isMobile) {
        this._applyScrollIndex(next, previous);
        // _applyScrollIndex only calls activateIndex while index changes;
        // force the first closed row open on initial pin entry as well.
        if (mustOpen && this._isListPinnedAtTop) this.activateIndex(next);
      } else if (mustOpen) {
        // The first mobile row has no index change (0 → 0), so it cannot
        // go through the deferred stepper. Open it as the pin establishes.
        this._settleStage(() => this.activateIndex(next));
      } else {
        this._pendingIndex = next;
        clearTimeout(this._commitTimer);
        this._commitTimer = setTimeout(() => this._commitPendingIndex(), 90);
      }
    }

    // Apply a scroll-driven row change: open it (while pinned) or, when
    // scrolling back up unpinned, close the rows below it. Extracted so
    // mobile can defer this whole effect to a settle point.
    _applyScrollIndex(idx, prev) {
      if (this._isListPinnedAtTop) {
        this.activateIndex(idx);
      } else if (idx < prev) {
        this.items.forEach((item, i) => {
          if (i <= idx) return;
          const d = item.querySelector('details');
          if (d && d.open) this.animateRow(d, false);
        });
        this.setActive(idx);
      }
    }

    // Mobile: run the deferred row change once scrolling has settled — but
    // only ONE row at a time. A fling that maps several rows ahead used to
    // commit straight to the far row, so the ones in between (row 0 in
    // particular, when you scroll in fast) never opened. Clamping to ±1 per
    // settle means each swipe advances exactly one accordion step, in
    // order, and _scrollIndex is pulled back so the next swipe continues
    // from there rather than the far position.
    _commitPendingIndex() {
      if (this._pendingIndex == null) return;
      const raw = this._pendingIndex;
      this._pendingIndex = null;
      const from = this.activeIndex;
      const idx = Math.max(from - 1, Math.min(from + 1, raw));
      this._scrollIndex = idx;
      if (idx === from) return;
      this._settleStage(() => this._applyScrollIndex(idx, from));
    }

    // Mobile: collapse (pin) / restore (unpin) the heading. The LAYOUT part
    // (height + the flex gap it holds) is set instantly so _settleStage can
    // measure the stage's real end position; only opacity eases (no layout
    // impact). The smooth motion of the list/stage settling into the new
    // layout is handled by _settleStage around the call site.
    collapseHeading(collapsed) {
      if (!this.heading || typeof gsap === 'undefined') return;
      gsap.killTweensOf(this.heading);
      if (collapsed) {
        gsap.set(this.heading, {
          height: 0,
          y: -12,
          marginBottom: this._contentGap ? -this._contentGap : 0,
        });
        gsap.to(this.heading, { opacity: 0, duration: 0.22, ease: 'power1.out', overwrite: 'auto' });
      } else {
        const h = this._headingHeight || this.heading.scrollHeight;
        gsap.set(this.heading, { height: h, y: 0, marginBottom: 0 });
        gsap.fromTo(this.heading, { opacity: 0 }, {
          opacity: 1,
          duration: 0.22,
          ease: 'power1.out',
          overwrite: 'auto',
          onComplete: () => gsap.set(this.heading, { clearProps: 'height,marginBottom,opacity,transform' }),
        });
      }
    }

    // Mobile: run `mutator` (which changes the list layout above the stage —
    // heading collapse, row open/close, consumed-row collapse) and then
    // glide the stage from its old position to its new one with easing,
    // instead of it snapping/jumping. The stage keeps its size and aspect
    // ratio — only translateY animates. mutator must be synchronous (see
    // animateRow, which is instant on mobile) so the "after" measurement is
    // the final layout.
    _settleStage(mutator) {
      if (!this._isMobile || !this.stage || typeof gsap === 'undefined') {
        mutator();
        return;
      }
      const before = this.stage.getBoundingClientRect().top;
      mutator();
      const after = this.stage.getBoundingClientRect().top;
      const dy = before - after;
      if (Math.abs(dy) < 1) return;
      gsap.fromTo(this.stage, { y: dy }, { y: 0, duration: 0.38, ease: 'power3.out', overwrite: 'auto' });
    }

    // Closes every accordion row and resets to row 0 — used only when
    // scrolling genuinely back above the section start. Routes through
    // animateRow()/setActive() so <collapsible-row>'s internal state and
    // the mobile "consumed rows" collapse (see updateConsumedItems) stay in
    // sync instead of desyncing from a raw `details.open = false`.
    closeAllAccordionItems() {
      clearTimeout(this._commitTimer);
      this._pendingIndex = null;
      this.detailsEls.forEach((details) => {
        if (details.open) this.animateRow(details, false); // fires 'toggle' -> bounceStage(-1)
      });
      this._scrollIndex = 0;
      if (this.activeIndex !== 0) {
        this.setActive(0);
      } else {
        // Already index 0 but rows may still be collapsed/consumed from a
        // mobile pass — restore them explicitly (setActive would no-op).
        this.updateConsumedItems(0);
        this.updateArrowPosition();
      }
    }

    // Fast snap the moment the list locks to the top: kills any mouse-
    // follow offset on the arrow and lets it latch onto the active row.
    onListPinnedAtTop() {
      // Safety net for fast/anchor scrolls: setupReveal()'s Intersection
      // Observer can land its callback late (or with the section already
      // scrolled past its 0.4 threshold) when the user jumps straight here,
      // leaving the stage stuck in its pre-reveal transform. Entering the
      // pinned range always forces the reveal — it's idempotent, and now
      // snaps to rest rather than animating when we're already pinned.
      this.playStageReveal();

      if (this.arrowIcon && typeof gsap !== 'undefined') {
        gsap.to(this.arrowIcon, { x: 0, y: 0, rotate: 0, duration: 0.22, ease: 'power4.out' });
      }
      this.updateArrowPosition();
    }

    // Open/close a row. Desktop: through <collapsible-row>'s own height
    // animation (assets/app.js) so scroll-driven steps ease open/shut like
    // a click does. Mobile: instant — the row height changes synchronously
    // so _settleStage can measure the final layout and glide the stage
    // there in one eased move (a 300ms height tween here would be a moving
    // target the stage had to chase, reading as a jump).
    animateRow(details, shouldOpen) {
      if (!details || !!details.open === shouldOpen) return;
      if (this._isMobile) {
        details.open = shouldOpen; // fires 'toggle' -> setupAccordionSync
        return;
      }
      const row = details.closest('collapsible-row');
      if (row && typeof row.onClick === 'function') {
        row.onClick(new Event('click'));
      } else {
        details.open = shouldOpen;
      }
    }

    activateIndex(index) {
      const item = this.items[index];
      if (!item) return;

      const details = item.querySelector('details');
      if (!details) {
        this.setActive(index);
        return;
      }

      if (this._isMobile) {
        // Do the whole cascade synchronously — close the others, open this
        // one, re-sync stage/arrow/pill/consumed rows — so the layout is
        // final by the time _settleStage measures it. The native 'toggle'
        // event fires async (next frame), which would land the big
        // consumed-row collapse AFTER the glide had already started.
        this.detailsEls.forEach((d) => { if (d !== details && d.open) d.open = false; });
        if (!details.open) details.open = true; // toggle handler still runs later (content reveal)
        this.setActive(index);
        return;
      }

      if (!details.open) {
        // No flag needed: setupAccordionSync's 'toggle' handler only
        // scrolls the page when wasUserInitiated() says a real tap/click
        // on this row just happened. A scroll-driven open never set that.
        this.animateRow(details, true); // fires 'toggle' (async) -> setupAccordionSync
      }
    }

    // Scrolls the page to wherever row `index` naturally opens in the
    // pinned track — so clicking a row (from any scroll position) lands
    // exactly where the scroll-driven state agrees with the click.
    scrollToIndex(index) {
      if (!this._scrollTrigger || !this.items.length) return;

      const count = this.items.length;
      const fadeEnd = this._headingFadeEnd || 0;
      const bandP = fadeEnd + ((index + 0.5) / count) * (1 - fadeEnd);
      const trigger = this._scrollTrigger;
      const targetY = trigger.start + bandP * (trigger.end - trigger.start);

      this._isAutoScrolling = true;
      clearTimeout(this._autoScrollTimeout);
      const stop = () => {
        this._isAutoScrolling = false;
        window.removeEventListener('scrollend', stop);
      };
      if ('onscrollend' in window) {
        window.addEventListener('scrollend', stop, { once: true });
      } else {
        this._autoScrollTimeout = setTimeout(stop, 800);
      }

      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }

    // Fast-in, slow-settle organic drop/lift with a slight overshoot.
    bounceStage(direction) {
      if (typeof gsap === 'undefined' || !this.stageInner) return;
      // Mobile: no stage bounce at all. The back.out overshoot read as the
      // image "springing" on every row open/close, and firing it mid-scroll
      // fed layout motion straight back into the scroll-driven row stepping
      // (the "springt hin und her"). Desktop keeps the bounce.
      if (window.matchMedia('(max-width: 767px)').matches) return;
      // The reveal (playStageReveal) animates this same element. A bounce
      // firing while it's still in flight — e.g. a fast anchor-scroll that
      // lands in the pinned range and opens a row via scroll — would
      // killTweensOf() the reveal timeline and freeze the stage
      // half-scaled/tilted. Skip until the reveal has fully settled;
      // nothing is visually lost by not bouncing then.
      if (!this._stageRevealSettled) return;

      gsap.killTweensOf(this.stageInner);
      gsap.to(this.stageInner, {
        y: direction * 14,
        duration: 0.5,
        delay: 0.1,
        ease: 'back.out(1.05)', // softer, gentler overshoot than before
        onComplete: () => {
          gsap.to(this.stageInner, { y: 0, duration: 0.7, ease: 'back.out(0.9)' });
        },
      });
    }

    // Springs the stage up from below with rotation + a stretchy overshoot
    // scale (back.out reads as "spring past, then settle" — the stretch
    // introduced in the parallax scroll section's image cards), landing at
    // a resting -5.78° tilt, the first time it scrolls into view — before
    // it locks into its pinned position. One-shot, like the text reveal —
    // not scroll-scrubbed.
    // Scroll-linked (parallax) reveal: as the stage scrolls into view, it
    // slides up while scaleX/scaleY animate INDEPENDENTLY (a real squash-
    // and-stretch, not a uniform scale) — starts short + narrow, stretches
    // past its resting size, then settles. Values are lerp-smoothed via a
    // rAF loop instead of snapping straight to the scroll-driven target,
    // so it trails and catches up with a soft, organic lag rather than
    // rigidly tracking the scrollbar 1:1.
    // One-shot reveal, fired from setupReveal()'s own IntersectionObserver
    // (same trigger as the list rows/dividers) rather than a separate
    // ScrollTrigger keyed off the stage's own position. It used to watch
    // for the stage itself crossing 'top 60%' of the viewport, but on
    // mobile the stage sat below the whole list in document flow, so that
    // point wasn't reached until well into the scroll — reading as "doesn't
    // trigger immediately". Tying it to the section's own entry instead
    // guarantees it always plays together with the list, right away.
    // NOT scroll-scrubbed — that made it feel stuck mid-transition if you
    // kept scrolling past its trigger point before it resolved. scaleX/
    // scaleY still animate independently (real squash-and-stretch, not a
    // uniform scale) for the "parallax stretch" look, just as tween
    // keyframes instead of a continuous scroll link.
    setupStageReveal() {
      this._playedStageReveal = false;   // "the reveal has been kicked off"
      this._stageRevealSettled = false;  // "the reveal has fully finished"
      this.playStageReveal = () => {}; // overwritten below when GSAP can drive it

      const canAnimate =
        typeof gsap !== 'undefined' &&
        this.stageInner &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!canAnimate) {
        // No animated entrance to protect — mark it done so bounceStage
        // (which bails until the reveal has settled) isn't disabled for
        // the whole section under reduced motion / no-GSAP.
        this._playedStageReveal = true;
        this._stageRevealSettled = true;
        return;
      }

      const restRotation = 5.78;
      const startRotation = restRotation + 16;

      const restingState = {
        scaleX: 1, scaleY: 1, y: 0, opacity: 1, rotation: restRotation,
      };

      gsap.set(this.stageInner, {
        transformOrigin: '50% 100%',
        scaleX: 0.68,
        scaleY: 0.5,
        y: 130,
        opacity: 0,
        rotation: startRotation,
      });

      this.playStageReveal = () => {
        if (this._playedStageReveal) return;
        this._playedStageReveal = true;

        // Scrolled straight into the section (anchor-link jump / very fast
        // scroll) so the list is already pinned at the top: there's nothing
        // to "reveal into view" — snap to the resting state instead of
        // running an entrance for something already fully on screen (which
        // otherwise plays out underneath the user, and can be killed
        // mid-flight by the first scroll-driven bounceStage).
        if (this._isListPinnedAtTop) {
          gsap.set(this.stageInner, restingState);
          this._stageRevealSettled = true;
          return;
        }

        gsap.timeline({ onComplete: () => { this._stageRevealSettled = true; } })
          // Overshoots past 1 on scaleX only, then settles — reads as a
          // stretch, not a plain scale-up.
          .to(this.stageInner, {
            scaleX: 1.05,
            scaleY: 0.94,
            y: 20,
            opacity: 1,
            rotation: restRotation,
            duration: 0.75,
            ease: 'power3.out',
          })
          .to(this.stageInner, {
            scaleX: 1,
            scaleY: 1,
            y: 0,
            duration: 0.4,
            ease: 'power2.out',
          });
      };
    }

    setActive(index) {
      if (index === this.activeIndex) return;
      this.stopArrowIdle();
      const fromIndex = this.activeIndex;
      this.activeIndex = index;

      this.items.forEach((item) => {
        item.classList.toggle('is-active', parseInt(item.dataset.index, 10) === index);
      });
      this.swapImage(fromIndex, index);
      this.updatePillStack(index);
      this.querySelectorAll('[data-usp-trigger][aria-pressed]').forEach((button) => {
        button.setAttribute('aria-pressed', parseInt(button.dataset.index, 10) === index ? 'true' : 'false');
      });

      this.updateConsumedItems(index);
      this.updateArrowPosition();
    }

    // Mobile only: rows already passed (index < active) collapse away
    // entirely, so the list doesn't keep growing taller as each row opens
    // — only the active row (+ its open content) and the still-unopened
    // rows below it stay visible. Reverses cleanly if the user scrolls
    // back up past a row. Desktop keeps every row visible (more room).
    updateConsumedItems(activeIndex) {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      // Last row active (mobile) = every earlier row has collapsed away, so
      // the list above the stage is short and the stage has drifted up too
      // far. The class lets the CSS ease its upward pull back (see
      // &.is-usp-last-active &__stage).
      this.classList.toggle(
        'is-usp-last-active',
        isMobile && this.items.length > 0 && activeIndex >= this.items.length - 1
      );

      if (typeof gsap === 'undefined') return;
      if (!isMobile) return;

      // Collect the rows that actually change state this call, then snap
      // them (see below — instant, not a 0.5s tween).
      const changed = [];
      this.items.forEach((item) => {
        const itemIndex = parseInt(item.dataset.index, 10);
        const shouldConsume = itemIndex < activeIndex;
        const isConsumed = item.classList.contains('is-consumed');
        if (shouldConsume === isConsumed) return;
        gsap.killTweensOf(item);
        changed.push({ item, shouldConsume });
      });
      if (!changed.length) return;

      // Snap, don't tween. An animated 0.5s height change here ran *while*
      // the user was still scrolling, so the list layout was a moving
      // target — the scroll-driven stepper kept re-sampling a shifting
      // position and flipped rows back and forth ("springt hin und her"),
      // and the stage visibly rode the motion. Setting the state instantly
      // leaves the layout still between steps.
      changed.forEach(({ item, shouldConsume }) => {
        if (shouldConsume) {
          item.classList.add('is-consumed');
          gsap.set(item, {
            height: 0,
            opacity: 0,
            marginBottom: 0,
            paddingTop: 0,
            paddingBottom: 0,
            overflow: 'hidden',
            willChange: 'auto',
          });
        } else {
          item.classList.remove('is-consumed');
          gsap.set(item, { clearProps: 'height,opacity,margin,padding,overflow,willChange' });
        }
      });
    }

    // Diagonal clip-path swipe (same technique as academy-color-slider),
    // not an opacity crossfade — both layers are fully painted and
    // correctly stacked (z-index) before anything animates, so there's
    // never a blank/transparent frame to flash through; only the visible
    // clip region sweeps.
    swapImage(fromIndex, toIndex) {
      const to = this.images[toIndex];
      if (!to) return;

      const from = this.images[fromIndex] !== to ? this.images[fromIndex] : null;

      if (!from || typeof gsap === 'undefined') {
        this.images.forEach((image) => {
          image.classList.toggle('is-active', image === to);
        });
        return;
      }

      // Rapid clicking/scrolling can fire several swaps before the
      // previous one's timeline finishes. Killing tweens only on this
      // call's two images left OTHER images (from an interrupted earlier
      // swap) stuck mid-transition — sometimes with no image ending up
      // visible at all (the "image disappears" bug). Now every call kills
      // ALL image tweens and force-resets anything that isn't this call's
      // from/to pair to a clean, resolved state first.
      gsap.killTweensOf(this.images);
      this.images.forEach((image) => {
        if (image === to || image === from) return;
        image.classList.remove('is-active');
        gsap.set(image, { visibility: 'hidden', zIndex: 0, clearProps: 'clipPath' });
      });

      // will-change only for the duration of the swipe (the CSS no longer
      // carries it permanently on every image).
      gsap.set(to, { visibility: 'visible', zIndex: 2, willChange: 'clip-path', clipPath: 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)' });
      gsap.set(from, { visibility: 'visible', zIndex: 1, willChange: 'clip-path', clearProps: 'clipPath' });

      gsap.timeline({
        defaults: { duration: this._isMobile ? 0.5 : 0.9, ease: 'power2.inOut' },
        onComplete: () => {
          from.classList.remove('is-active');
          to.classList.add('is-active');
          gsap.set(from, { visibility: 'hidden', zIndex: 0, clearProps: 'clipPath', willChange: 'auto' });
          gsap.set(to, { zIndex: 1, clearProps: 'clipPath', willChange: 'auto' });
        },
      })
        .fromTo(to,
          { clipPath: 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)' },
          { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, -30% 100%)' })
        .fromTo(from,
          { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' },
          { clipPath: 'polygon(0% 0%, 0% 0%, -30% 100%, 0% 100%)' }, '<');
    }

    // Keeps the decorative arrow vertically aligned with the active row —
    // its "latch onto the row" behavior once the list is pinned at top.
    updateArrowPosition() {
      if (!this.arrow) return;

      const activeItem = this.items[this.activeIndex];
      const wrap = activeItem && activeItem.closest('.academy-usp-list__list-wrap');
      if (!activeItem || !wrap) return;

      const itemRect = activeItem.getBoundingClientRect();
      const wrapRect = wrap.getBoundingClientRect();
      const y = itemRect.top - wrapRect.top + itemRect.height / 2 - 12;
      this.style.setProperty('--usp-arrow-y', `${y}px`);
    }


    // Split-flap/scoreboard "stack" text swap for the pill — same mechanic
    // as academy-upgrade-hero's stack reveal: every row already sits
    // stacked in the markup, &__pill-clip masks it down to exactly one
    // row's height (measured here at runtime, since it varies with
    // content/font), and updatePillStack slides &__pill-stack so the
    // active row lands in the visible window instead of the pill just
    // riding along inside the image's own clip-path swipe.
    setupPillStack() {
      if (!this.pill || !this.pillRows.length) return;
      this.updatePillStack(this.activeIndex, true);
      // The first measure can land before the webfont has swapped in,
      // sizing the pill to the fallback face's metrics. Re-measure once
      // fonts are ready so the initial pill isn't left clipped.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.updatePillStack(this.activeIndex, true));
      }
    }

    updatePillStack(index, immediate) {
      if (!this.pill || !this.pillStack || !this.pillRows.length) return;

      const row = this.pillRows.find((r) => parseInt(r.dataset.index, 10) === index);
      if (!row) {
        // No pill text configured for this block — hide the badge instead
        // of leaving it frozen showing a stale row's text.
        if (typeof gsap !== 'undefined') gsap.to(this.pill, { autoAlpha: 0, duration: 0.3 });
        return;
      }
      if (typeof gsap !== 'undefined') gsap.to(this.pill, { autoAlpha: 1, duration: 0.3 });

      this.pill.style.setProperty('--pill-bg', row.dataset.bg);
      this.pill.style.setProperty('--pill-color', row.dataset.color);
      this.pill.style.setProperty('--usp-pill-border-color', row.dataset.border);

      // Hug the active label: size the pill to this row's own (nowrap)
      // text width plus its padding + border, and tween that width so a
      // shorter/longer next label doesn't make the right-anchored pill
      // jump.
      //
      // Measuring this correctly needs care on two fronts:
      //  - The row is display:block inside &__pill-clip (width:100% of the
      //    pill), so a plain row.scrollWidth can only ever report MORE
      //    than that box, never less — the pill would grow for a long
      //    label but never shrink back for a shorter one. Temporarily
      //    flipping the row to inline-block makes its box shrink-wrap the
      //    text, so scrollWidth then reports the true text width in both
      //    directions.
      //  - It must be a LAYOUT metric (scrollWidth/offsetWidth), not
      //    getBoundingClientRect()/Range — the pill lives inside
      //    [data-usp-stage-inner], which setupStageReveal() pre-scales
      //    with a GSAP transform (scaleX:0.68) before this first runs on
      //    load. A rect-based measure picks up that transform and sizes
      //    the very first pill ~30% too narrow (clipped until something
      //    re-measures it later); scrollWidth ignores the transform.
      const cs = getComputedStyle(this.pill);
      const chrome =
        parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) +
        parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
      const prevDisplay = row.style.display;
      row.style.display = 'inline-block';
      const textW = row.scrollWidth;
      row.style.display = prevDisplay;
      const targetW = Math.ceil(textW + chrome) + 1;
      if (typeof gsap !== 'undefined') {
        if (immediate) {
          gsap.set(this.pill, { '--usp-pill-w': targetW + 'px' });
        } else {
          gsap.to(this.pill, { '--usp-pill-w': targetW + 'px', duration: 0.6, ease: 'power4.inOut' });
        }
      } else {
        this.pill.style.setProperty('--usp-pill-w', targetW + 'px');
      }

      // offsetTop reflects each row's actual, untransformed layout
      // position — unlike getBoundingClientRect, which would already
      // include the very translateY being computed here. A prior version
      // stepped by a separately-measured "row height" instead, and any
      // small rounding mismatch between that value and the real row
      // spacing compounded with each row — landing worst on the *last*
      // row (furthest down the stack), which is exactly the one that was
      // getting clipped out while earlier rows still looked fine.
      const targetY = -(row.offsetTop - this.pillRows[0].offsetTop);

      if (typeof gsap === 'undefined' || immediate) {
        if (typeof gsap !== 'undefined') gsap.set(this.pillStack, { y: targetY });
        return;
      }

      gsap.to(this.pillStack, { y: targetY, duration: 0.6, ease: 'power4.inOut' });
    }

    // A quirky little entrance for the arrow itself — not a plain fade,
    // an elastic wobble-in — independent of (and running before) the idle
    // "try me" loop.
    setupArrowEntrance() {
      if (!this.arrowIcon || typeof gsap === 'undefined') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      gsap.set(this.arrowIcon, { opacity: 0, scale: 0.4, rotate: -35 });
      gsap.to(this.arrowIcon, {
        opacity: 1,
        scale: 1,
        rotate: 0,
        duration: 0.9,
        delay: 0.5,
        ease: 'elastic.out(1, 0.45)',
      });
    }

    // Gentle "try me" nudge on the arrow before the user has scrolled/
    // interacted with the list — runs on a separate inner element so it
    // never fights the outer element's position-tracking transform (CSS
    // var-driven translateY). Stops the moment real tracking begins, i.e.
    // the first time the arrow actually moves to a new active row.
    startArrowIdle() {
      if (!this.arrowIcon || typeof gsap === 'undefined') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // Mobile: no "try me" arrow wiggle — one more thing animating on the
      // compositor during scroll, and there's no cursor to invite anyway.
      if (window.matchMedia('(max-width: 767px)').matches) return;

      this._arrowIdleTween = gsap.to(this.arrowIcon, {
        x: 6,
        y: -6,
        duration: 0.9,
        delay: 1.4,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });
    }

    stopArrowIdle() {
      if (!this._arrowIdleTween) return;
      this._arrowIdleTween.kill();
      this._arrowIdleTween = null;
      if (this.arrowIcon && typeof gsap !== 'undefined') {
        gsap.to(this.arrowIcon, { x: 0, y: 0, duration: 0.3, ease: 'power2.out' });
      }
    }

    // While the heading is still visible (list not yet pinned at top),
    // desktop-only: the arrow follows the mouse around the section, and
    // rotates to -145° to "aim" at whichever row is currently hovered.
    // Once the list locks to the top, this hands off to updateArrowPosition
    // (see onListPinnedAtTop, which snaps it there fast).
    setupArrowMouseFollow() {
      if (!this.arrow || !this.arrowIcon || typeof gsap === 'undefined') return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      let rafId = null;
      let lastEvent = null;

      this.addEventListener('pointermove', (event) => {
        if (this._isListPinnedAtTop) return;
        this.stopArrowIdle();
        lastEvent = event;
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          if (!lastEvent) return;
          const rect = this.arrow.getBoundingClientRect();
          const baseX = rect.left + rect.width / 2;
          const baseY = rect.top + rect.height / 2;
          gsap.to(this.arrowIcon, {
            x: lastEvent.clientX - baseX,
            y: lastEvent.clientY - baseY,
            duration: 0.35,
            ease: 'power3.out',
            overwrite: 'auto',
          });
        });
      });

      // Not gated to the pre-pin phase like the mouse-follow above — this
      // rotate cue works on hover for every row, pinned or not, since it
      // doesn't fight the position-tracking transform (different element).
      this.items.forEach((item) => {
        item.addEventListener('mouseenter', () => {
          gsap.to(this.arrowIcon, { rotate: -145, duration: 0.4, ease: 'power3.out', overwrite: 'auto' });
        });
        item.addEventListener('mouseleave', () => {
          gsap.to(this.arrowIcon, { rotate: 0, duration: 0.4, ease: 'power3.out', overwrite: 'auto' });
        });
      });
    }

    // Tracks pointer velocity across the track, then the instant the
    // pointer lands on the stage, throws it via InertiaPlugin using that
    // approach velocity + an entry-angle spin — settles back to rest on
    // its own.
    // Desktop-pointer only: on touch a scroll IS a pointermove, so this
    // added per-frame velocity math to the scroll path and pointerenter
    // fired an inertia fling underneath the scroll gesture. Gated like the
    // arrow mouse-follow.
    setupStagePointer() {
      if (typeof gsap === 'undefined' || typeof InertiaPlugin === 'undefined') return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      const item = this.querySelector('[data-inertia-item]');
      const child = this.querySelector('[data-inertia-item-child]');
      if (!item || !child) return;

      const restRotation = 5.78;
      const clampVelocity = gsap.utils.clamp(-1080, 1080);
      const clampRotation = gsap.utils.clamp(-60, 60);

      let lastX = 0;
      let lastY = 0;
      let deltaX = 0;
      let deltaY = 0;
      let rafId = null;

      this.addEventListener('pointermove', (event) => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          deltaX = event.clientX - lastX;
          deltaY = event.clientY - lastY;
          lastX = event.clientX;
          lastY = event.clientY;
          rafId = null;
        });
      });

      item.addEventListener('pointerenter', (event) => {
        const rect = child.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const offsetX = event.clientX - centerX;
        const offsetY = event.clientY - centerY;
        const spin = (offsetX * deltaY - offsetY * deltaX) / (Math.hypot(offsetX, offsetY) || 1);

        gsap.to(child, {
          inertia: {
            x: { velocity: clampVelocity(30 * deltaX), end: 0 },
            y: { velocity: clampVelocity(30 * deltaY), end: 0 },
            rotation: { velocity: clampRotation(15 * spin), end: restRotation },
            resistance: 180,
          },
        });
      });
    }
  }

  customElements.define('academy-usp-list', AcademyUspList);
}
